import { useMemo } from 'react';

import { OccupationType } from 'api/types/entity';
import { isFinanceProject, isGlobalProject } from 'config';
import { formatToDate, lodash, parseFullName } from 'helpers';
import { useTranslation } from 'libs/i18n';
import { OrderSubmissionSection } from 'modules/client/common/components';
import {
  WithdrawalAccountsSection,
  PersonalDataSection,
  CitizenshipSection,
  PlaceOfBirthSection,
  ContactsSection,
  PepConnectionSection,
  TaxResidencySection,
  ActivitySection,
  AdditionalDocumentsSection,
  AccountCodesSection,
  IdentityDocumentSection,
  ServiceContractSection,
  RiskLevelSection,
  ResultCheckSection,
  ResidentialAddressSection,
  RegistrationAddressSection,
  USTaxResidencySection,
  W8BENFormSection,
  KYCSection,
  BiographySection,
  BankAccountsSection,
  DebtsSection,
} from 'modules/client/common/components/sections';
import {
  RepresentativeData,
  RepresentativeSection,
} from 'modules/client/common/components/sections/RepresentativeSection/RepresentativeSection';
import {
  CountryRiskBadges,
  CountryRiskRole,
  getMainAddressCountryRisk,
} from 'modules/client/common/feature';

import { useFormContext } from '../../forms/clientForm';
import {
  FinancialPositionSection,
  GeneralSection,
  RiskProfileSection,
  SourcesOfIncomeSection,
  KnowledgeAndExperienceSection,
  ClientDocumentsSection,
  OpenAccountOrderSection,
  FinancingSourcesSection,
} from '../sections';
import { BusinessRelationshipSection } from '../sections/BusinessRelationshipSection';

import {
  getClientInfo,
  getClientOtherFormSectionKeys,
  getClientPersonalAccountFormSectionKeys,
  getClientRiskLevelFormSectionKeys,
  getClientContactsAndAddressesFormSectionKeys,
} from './helpers';
import { ClientSectionsProps } from './types';

export const useClientSections = ({
  countries,
  countriesLoading,
  kycData,
  onboardingData,
  managers,
  managersLoading,
  visibleOnboardingStatuses,
  hideActivitySection,
  isEditablePersonalAccountNumber,
  mainInfo,
  representativeInfo,
  onClickRepresentative,
}: ClientSectionsProps) => {
  const { t } = useTranslation();
  const { values } = useFormContext();

  const isGlobal = isGlobalProject();
  const isFinance = isFinanceProject();

  const isEntrepreneur =
    values.biography?.occupation?.value === OccupationType.entrepreneur;

  const representativeInfoContent: RepresentativeData = useMemo(() => {
    const representativeCountryRisk = getMainAddressCountryRisk(
      representativeInfo?.residentialAddress?.country,
      representativeInfo?.registrationAddress?.country
    );

    return {
      name: parseFullName({
        firstName: representativeInfo?.firstName ?? '',
        lastName: representativeInfo?.lastName ?? '',
        middleName: representativeInfo?.middleName,
      }),
      fields: [
        {
          label: t('client.client.representative.emailLabel'),
          value: representativeInfo?.email,
        },
        {
          label: t('client.client.representative.phoneLabel'),
          value: representativeInfo?.phone,
        },
        {
          label: t('client.client.representative.countryLabel'),
          value: representativeCountryRisk.country?.name,
          valueAdornment: (
            <CountryRiskBadges
              {...representativeCountryRisk}
              role={CountryRiskRole.ClientRepresentative}
            />
          ),
        },
        {
          label: t('client.client.representative.documentExpiryLabel'),
          value: representativeInfo?.basisForRepresentation?.dateOfExpiration
            ? formatToDate(
                representativeInfo.basisForRepresentation.dateOfExpiration
              )
            : undefined,
        },
      ],
    };
  }, [representativeInfo, t]);

  const tabs = useMemo(
    () =>
      lodash.compact([
        {
          label: t('client.client.sections.personalAccount'),
          id: 'personalAccount',
          content: (
            <>
              <AccountCodesSection
                isEditablePersonalAccountNumber={
                  isEditablePersonalAccountNumber
                }
                managers={managers}
                managersLoading={managersLoading}
              />
              {isFinance && <OpenAccountOrderSection />}
              <ServiceContractSection />
            </>
          ),
          formSectionKeys: getClientPersonalAccountFormSectionKeys(),
        },
        {
          label: t('client.client.sections.basic'),
          id: 'basic',
          content: (
            <>
              <PersonalDataSection enabledGender={isGlobal} />
              <CitizenshipSection
                countries={countries}
                countriesLoading={countriesLoading}
              />
              <IdentityDocumentSection
                enabledDocuments
                countries={countries}
                countriesLoading={countriesLoading}
              />
              {isGlobal && <USTaxResidencySection />}
              <TaxResidencySection
                countries={countries}
                countriesLoading={countriesLoading}
                enabledDocuments={isGlobal}
              />
              <PlaceOfBirthSection
                countries={countries}
                countriesLoading={countriesLoading}
              />
            </>
          ),
          formSectionKeys: [
            'personalData',
            'citizenships',
            'identityDocument',
            'taxResidences',
            'placeOfBirth',
          ],
        },
        {
          label: t('client.client.sections.withdrawalAccounts'),
          id: 'withdrawalAccounts',
          content: <WithdrawalAccountsSection />,
          formSectionKeys: ['withdrawalAccounts'],
        },
        representativeInfo && {
          label: t('client.client.sections.representative'),
          id: 'representative',
          content: (
            <RepresentativeSection
              representativeData={representativeInfoContent}
              onClickRepresentative={onClickRepresentative}
            />
          ),
          formSectionKeys: [],
        },
        {
          label: t('client.client.sections.contactsAndAddresses'),
          id: 'contactsAndAddresses',
          content: (
            <>
              {!isGlobal && (
                <RegistrationAddressSection
                  countries={countries}
                  countriesLoading={countriesLoading}
                />
              )}
              <ResidentialAddressSection
                enabledFiles
                countries={countries}
                countriesLoading={countriesLoading}
              />
              <ContactsSection />
              <OrderSubmissionSection />
            </>
          ),
          formSectionKeys: getClientContactsAndAddressesFormSectionKeys(),
        },
        {
          label: t('client.client.sections.classification'),
          id: 'classification',
          content: (
            <GeneralSection
              onboardingData={onboardingData}
              showClassification={isGlobal}
              showClientType={isGlobal}
            />
          ),
          formSectionKeys: ['general'],
        },
        {
          label: t('client.client.sections.documents'),
          id: 'documents',
          content: (
            <>
              <ClientDocumentsSection />
              <AdditionalDocumentsSection />
            </>
          ),
          formSectionKeys: ['documentsFiles', 'additionalDocuments'],
        },
        {
          label: t('client.client.sections.riskLevel'),
          id: 'riskLevel',
          content: (
            <>
              <RiskLevelSection />
              <KYCSection kycData={kycData} />
              <PepConnectionSection notRequestInformation={isGlobal} />
              {isGlobal && <SourcesOfIncomeSection />}
            </>
          ),
          formSectionKeys: getClientRiskLevelFormSectionKeys(),
        },
        {
          label: t('client.client.sections.resultCheck'),
          id: 'resultCheck',
          content: <ResultCheckSection statuses={visibleOnboardingStatuses} />,
          formSectionKeys: ['resultCheckSection'],
        },
        {
          label: t('client.client.sections.other'),
          id: 'other',
          content: (
            <>
              {!hideActivitySection && <ActivitySection brightCodeEnabled />}
              {isFinance && <BiographySection />}
              {isFinance && (
                <BankAccountsSection
                  countries={countries}
                  countriesLoading={countriesLoading}
                />
              )}
              {isFinance && <FinancingSourcesSection />}
              {isFinance && <DebtsSection isEntrepreneur={isEntrepreneur} />}
              {isGlobal && <W8BENFormSection formType="W8BENForm" />}
              {isGlobal && <BusinessRelationshipSection />}
              {isGlobal && <RiskProfileSection />}
              {isGlobal && <KnowledgeAndExperienceSection />}
              {isGlobal && <FinancialPositionSection />}
            </>
          ),
          formSectionKeys: getClientOtherFormSectionKeys(),
        },
      ]),
    [
      t,
      isEditablePersonalAccountNumber,
      managers,
      managersLoading,
      isFinance,
      isGlobal,
      countries,
      countriesLoading,
      representativeInfo,
      representativeInfoContent,
      onClickRepresentative,
      onboardingData,
      kycData,
      visibleOnboardingStatuses,
      hideActivitySection,
      isEntrepreneur,
    ]
  );

  const info = useMemo(() => getClientInfo(mainInfo, t), [mainInfo, t]);

  return { tabs, info };
};
